import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LinearProgress } from "@mui/material";
import { Stethoscope } from "lucide-react";
import { useSnackbar } from "notistack";

import Button from "../../../../componentsv2/Button";
import { Input } from "../../../../componentsv2/Input";
import { Select } from "../../../../componentsv2/Select";
import ChipTipoProcedimento from "../ChipTipoProcedimento";

import { api } from "../../../../services/Api";
import getApiErrorMessage from "../../../../utils/getApiErrorMessage";
import { obterDescricaoEvolucaoSessao } from "../../utils/evolucaoFormatters";

function formatarDataHora(valor) {
  if (!valor) return "";
  return new Date(valor).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function badgeStyle(status) {
  const realizada = status === "REALIZADA";
  return {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    backgroundColor: realizada ? "#E8F5E9" : "#FFF3E0",
    color: realizada ? "#2E7D32" : "#E65100",
  };
}

function resumoBadgeStyle(status) {
  const realizada = status === "REALIZADA";
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: "0.01em",
    backgroundColor: realizada ? "#05AF3A" : "#D07A00",
    color: "#FFFFFF",
  };
}

function labelLocalAtendimento(local) {
  if (local === "DOMICILIO") return "DOMICILIAR";
  if (local === "SEDE") return "SEDE";
  return local || "—";
}

function CartaoSessaoColunas({ sessao, nome, tipo, localLabel, enfermeiroPadrao }) {
  const realizada = sessao.status === "REALIZADA";
  const evolucao = obterDescricaoEvolucaoSessao(sessao);
  const nomeEnfermeiro = sessao.enfermeiro?.nome || enfermeiroPadrao || "—";

  return (
    <div
      style={{
        border: "1px solid #DDE6F5",
        borderRadius: "12px",
        padding: "14px 18px",
        marginBottom: "12px",
        backgroundColor: "#FFFFFF",
        boxShadow: "0 1px 2px rgba(51, 51, 68, 0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", flexWrap: "wrap" }}>
        <ChipTipoProcedimento tipo={tipo} mostrarSigla={false} />
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            padding: "2px 10px",
            borderRadius: "4px",
            backgroundColor: "#F3F5F8",
            color: "#5C6E91",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {localLabel}
        </span>
      </div>

      <div
        style={{
          fontSize: "16px",
          fontWeight: 700,
          lineHeight: 1.2,
          color: "#111111",
          textTransform: "uppercase",
          marginBottom: realizada && evolucao ? "8px" : "4px",
        }}
      >
        {nome}
        {sessao.numeroSessao > 1 ? ` — Sessão ${sessao.numeroSessao}` : ""}
      </div>

      {realizada && evolucao && (
        <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#4E5B84", lineHeight: 1.45 }}>
          {evolucao}
        </p>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#8D97B3",
          textTransform: "uppercase",
        }}
      >
        <Stethoscope size={14} strokeWidth={1.75} />
        <span>
          {realizada && sessao.realizadoEm
            ? `${nomeEnfermeiro} · ${formatarDataHora(sessao.realizadoEm)}`
            : nomeEnfermeiro}
        </span>
      </div>
    </div>
  );
}

export default function ProgressoSessoes({
  agendamentoId,
  enfermeiroIdDefault,
  enfermeiroNomeDefault = "",
  readOnly = false,
  layoutColunas = false,
  localAtendimento = "",
  semTitulo = false,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvandoId, setSalvandoId] = useState(null);
  const [enfermeiros, setEnfermeiros] = useState([]);
  const [evolucoesLocais, setEvolucoesLocais] = useState({});
  const [enfermeirosLocais, setEnfermeirosLocais] = useState({});

  const progresso = useMemo(() => {
    const todas = grupos.flatMap(g => g.sessoes || []);
    const realizadas = todas.filter(s => s.status === "REALIZADA").length;
    return { realizadas, total: todas.length };
  }, [grupos]);

  const percentual = progresso.total > 0 ? (progresso.realizadas / progresso.total) * 100 : 0;
  const progressoConcluido = progresso.total > 0 && progresso.realizadas >= progresso.total;
  const localLabel = labelLocalAtendimento(localAtendimento);

  const sessoesPorStatus = useMemo(() => {
    const todas = grupos.flatMap(grupo =>
      (grupo.sessoes || []).map(sessao => ({
        ...sessao,
        nome: grupo.nome,
        tipo: grupo.tipo,
      }))
    );

    return {
      pendentes: todas.filter(sessao => sessao.status === "PENDENTE"),
      realizadas: todas.filter(sessao => sessao.status === "REALIZADA"),
    };
  }, [grupos]);

  const carregarSessoes = useCallback(async () => {
    if (!agendamentoId) return;

    try {
      setLoading(true);
      const {
        data: { data },
      } = await api.get(`/v2/agendaCare/agendamento/${agendamentoId}/sessoes`);

      const lista = Array.isArray(data) ? data : [];
      setGrupos(lista);

      const evolucoes = {};
      const enfs = {};
      lista.forEach(grupo => {
        (grupo.sessoes || []).forEach(sessao => {
          evolucoes[sessao.id] = obterDescricaoEvolucaoSessao(sessao);
          enfs[sessao.id] = sessao.enfermeiroId || enfermeiroIdDefault || "";
        });
      });
      setEvolucoesLocais(evolucoes);
      setEnfermeirosLocais(enfs);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Não foi possível carregar as sessões."), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [agendamentoId, enfermeiroIdDefault, enqueueSnackbar]);

  useEffect(() => {
    carregarSessoes();
  }, [carregarSessoes]);

  useEffect(() => {
    async function carregarEnfermeiros() {
      if (readOnly) return;

      try {
        const {
          data: { data },
        } = await api.get("/v2/agendaCare/enfermeiros");
        const lista = Array.isArray(data) ? data : [];
        const opcoes = lista.map(enfermeiro => {
          const usuario = enfermeiro?.usuario;
          const nomeUsu =
            usuario?.funcionario?.nome_usu ||
            usuario?.funcionarios?.nome_usu ||
            usuario?.username ||
            "";
          const nomeCompleto = String(nomeUsu).trim().toUpperCase() || "—";
          const partes = nomeCompleto.split(/\s+/).filter(Boolean);
          return {
            id: enfermeiro.id,
            nome: partes.length > 1 ? `${partes[0]} ${partes[1]}` : partes[0] || "—",
          };
        });
        setEnfermeiros(opcoes);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, "Não foi possível carregar enfermeiros."), {
          variant: "warning",
        });
      }
    }

    carregarEnfermeiros();
  }, [enqueueSnackbar, readOnly]);

  const atualizarSessao = async (sessao, novoStatus) => {
    if (!agendamentoId || readOnly) return;

    try {
      setSalvandoId(sessao.id);
      await api.patch(`/v2/agendaCare/agendamento/${agendamentoId}/sessoes/${sessao.id}`, {
        status: novoStatus,
        descricao: evolucoesLocais[sessao.id] ?? obterDescricaoEvolucaoSessao(sessao) ?? "",
        enfermeiroId: enfermeirosLocais[sessao.id] || enfermeiroIdDefault || null,
        realizadoEm: novoStatus === "REALIZADA" ? new Date().toISOString() : null,
      });
      await carregarSessoes();
      enqueueSnackbar(
        novoStatus === "REALIZADA" ? "Sessão marcada como realizada." : "Sessão voltou para pendente.",
        { variant: "success" }
      );
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        enqueueSnackbar("Esta sessão foi alterada por outro usuário. Atualizando...", {
          variant: "warning",
        });
        await carregarSessoes();
        return;
      }
      enqueueSnackbar(getApiErrorMessage(error, "Não foi possível atualizar a sessão."), {
        variant: "error",
      });
    } finally {
      setSalvandoId(null);
    }
  };

  if (!agendamentoId) return null;

  if (readOnly && layoutColunas) {
    const pendentes = progresso.total - progresso.realizadas;

    return (
      <div style={{ width: "100%", maxWidth: 980, margin: "0 auto" }}>
        <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#333344", textAlign: "left" }}>
          {progresso.realizadas} de {progresso.total} procedimentos realizados
        </p>

        <LinearProgress
          variant="determinate"
          value={percentual}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#E0E0E0",
            marginBottom: "16px",
            "& .MuiLinearProgress-bar": {
              backgroundColor: progressoConcluido ? "#43A047" : "#003399",
            },
          }}
        />

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap", justifyContent: "flex-start" }}>
          <span style={resumoBadgeStyle("REALIZADA")}>
            {progresso.realizadas} Realizados
          </span>
          <span style={resumoBadgeStyle("PENDENTE")}>
            {pendentes} Pendentes
          </span>
        </div>

        {loading && <p style={{ color: "#666", fontSize: "14px" }}>Carregando sessões...</p>}

        {!loading && progresso.total === 0 && (
          <p style={{ color: "#666", fontSize: "14px" }}>
            Nenhuma sessão para procedimentos ou medicamentos neste agendamento. Deslocamentos,
            adicionais e itens N/A não geram sessões.
          </p>
        )}

        {!loading && progresso.total > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "24px" }}>
            <div>
              <h5 style={{ margin: "0 0 12px 0", fontSize: "24px", fontWeight: 700, lineHeight: 1.15, color: "#111111" }}>
                Sessões pendentes
              </h5>
              {sessoesPorStatus.pendentes.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#999" }}>Nenhuma sessão pendente.</p>
              ) : (
                sessoesPorStatus.pendentes.map(sessao => (
                  <CartaoSessaoColunas key={sessao.id} sessao={sessao} nome={sessao.nome} tipo={sessao.tipo} localLabel={localLabel} enfermeiroPadrao={enfermeiroNomeDefault} />
                ))
              )}
            </div>
            <div>
              <h5 style={{ margin: "0 0 12px 0", fontSize: "24px", fontWeight: 700, lineHeight: 1.15, color: "#111111" }}>
                Sessões realizadas
              </h5>
              {sessoesPorStatus.realizadas.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#999" }}>Nenhuma sessão realizada.</p>
              ) : (
                sessoesPorStatus.realizadas.map(sessao => (
                  <CartaoSessaoColunas key={sessao.id} sessao={sessao} nome={sessao.nome} tipo={sessao.tipo} localLabel={localLabel} enfermeiroPadrao={enfermeiroNomeDefault} />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {!semTitulo && <h4 style={{ margin: "8px 0 12px 0" }}>Progresso e Sessões</h4>}

      <div
        style={{
          border: "1px solid #E0E0E0",
          borderRadius: "10px",
          padding: "16px",
          backgroundColor: "#FAFAFA",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
          <span style={{ fontWeight: 600 }}>
            {progresso.realizadas} de {progresso.total} realizadas
          </span>
          <span style={{ color: "#616161" }}>{Math.round(percentual)}%</span>
        </div>

        <LinearProgress
          variant="determinate"
          value={percentual}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "#FFE0B2",
            "& .MuiLinearProgress-bar": {
              backgroundColor: progressoConcluido ? "#43A047" : "#003399",
            },
          }}
        />

        {loading && (
          <p style={{ marginTop: "12px", color: "#666", fontSize: "14px" }}>Carregando sessões...</p>
        )}

        {!loading && progresso.total === 0 && (
          <p style={{ marginTop: "12px", color: "#666", fontSize: "14px" }}>
            Nenhuma sessão para procedimentos ou medicamentos neste agendamento. Deslocamentos,
            adicionais e itens N/A não geram sessões.
          </p>
        )}

        {!loading &&
          grupos.map(grupo => (
            <div
              key={grupo.agendamentoProcedimentoId}
              style={{
                marginTop: "16px",
                borderTop: "1px solid #EEEEEE",
                paddingTop: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <strong>{grupo.nome}</strong>
                <ChipTipoProcedimento tipo={grupo.tipo} />
                <span style={{ color: "#757575", fontSize: "13px" }}>Qtd: {grupo.quantidade}</span>
              </div>

              {(grupo.sessoes || []).map(sessao => (
                <div
                  key={sessao.id}
                  style={{
                    border: "1px solid #ECEFF1",
                    borderRadius: "8px",
                    padding: "12px",
                    marginBottom: "10px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>Sessão {sessao.numeroSessao}</span>
                    <span style={badgeStyle(sessao.status)}>
                      {sessao.status === "REALIZADA" ? "Realizada" : "Pendente"}
                    </span>
                  </div>

                  {sessao.status === "REALIZADA" && (
                    <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#616161" }}>
                      {sessao.enfermeiro?.nome ? `Enfermeiro: ${sessao.enfermeiro.nome}` : ""}
                      {sessao.realizadoEm ? ` · ${formatarDataHora(sessao.realizadoEm)}` : ""}
                    </p>
                  )}

                  {!readOnly && (
                    <>
                      <div style={{ marginTop: "10px", maxWidth: 320 }}>
                        <Select
                          label="Enfermeiro"
                          variant="standard"
                          value={enfermeirosLocais[sessao.id] || ""}
                          onChange={e =>
                            setEnfermeirosLocais(prev => ({
                              ...prev,
                              [sessao.id]: e.target.value,
                            }))
                          }
                          arrayOptions={enfermeiros}
                          textAttribute="nome"
                          valueAttribute="id"
                          disabled={salvandoId === sessao.id}
                        />
                      </div>

                      <div style={{ marginTop: "10px" }}>
                        <Input
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={4}
                          label="Evolução da sessão"
                          variant="outlined"
                          value={evolucoesLocais[sessao.id] ?? ""}
                          onChange={e =>
                            setEvolucoesLocais(prev => ({
                              ...prev,
                              [sessao.id]: e.target.value,
                            }))
                          }
                          disabled={salvandoId === sessao.id}
                        />
                      </div>

                      <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                        {sessao.status === "PENDENTE" ? (
                          <Button
                            type="button"
                            variant="contained"
                            color="blue"
                            disabled={salvandoId === sessao.id}
                            onClick={() => atualizarSessao(sessao, "REALIZADA")}
                          >
                            Marcar como realizada
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outlined"
                            disabled={salvandoId === sessao.id}
                            onClick={() => atualizarSessao(sessao, "PENDENTE")}
                          >
                            Voltar para pendente
                          </Button>
                        )}
                      </div>
                    </>
                  )}

                  {readOnly && obterDescricaoEvolucaoSessao(sessao) && (
                    <p style={{ marginTop: "8px", fontSize: "14px", color: "#424242" }}>
                      {obterDescricaoEvolucaoSessao(sessao)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}
